import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Mail, ArrowUpRight } from "lucide-react";
import { CONTACT } from "@/lib/data";
import { Newsletter } from "@/components/Newsletter";

const FOOTER_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/deals", label: "Deals" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  return (
    <footer className="bg-[#0A0A0A] border-t border-white/10" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-20 md:pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5">Get in touch</p>
            <h3 className="font-display uppercase text-3xl md:text-4xl leading-[1.05] tracking-tight">
              One fixture or one thousand — we&apos;re ready to help.
            </h3>
            <Link
              to="/contact"
              data-testid="footer-quote-cta"
              className="mt-8 inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-6 py-4 hover:bg-[#b00000] transition-colors duration-300"
            >
              Request a Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5">Explore</p>
            <ul className="space-y-3">
              {FOOTER_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="link-sweep text-white/70 hover:text-white uppercase tracking-[0.1em] text-sm transition-colors duration-300"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3 lg:col-start-10">
            <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5">Contact</p>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gold mt-1 shrink-0" />
                <a href={CONTACT.phoneHref} className="link-sweep text-white/80 hover:text-white" data-testid="footer-phone">
                  {CONTACT.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-gold mt-1 shrink-0" />
                <a href={CONTACT.emailHref} className="link-sweep text-white/80 hover:text-white break-all">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gold mt-1 shrink-0" />
                <a
                  href={CONTACT.mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-sweep text-white/80 hover:text-white"
                  data-testid="footer-address"
                >
                  {CONTACT.street}
                  <br />
                  {CONTACT.city}, {CONTACT.region} {CONTACT.postalCode}
                  <br />
                  {CONTACT.country}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-gold mt-1 shrink-0" />
                <span className="text-white/80">{CONTACT.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 md:mt-20 pt-12 border-t border-white/10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-4">Weekly Deals</p>
            <h3 className="font-display uppercase text-2xl md:text-3xl leading-[1.05] tracking-tight">
              Get our deals in your inbox
            </h3>
            <p className="mt-3 text-white/50 text-sm max-w-md">
              Join the list for wholesale specials and our weekly flyer. No minimum order, no spam.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-center">
            <Newsletter variant="footer" />
          </div>
        </div>

        <div className="mt-16 md:mt-24">
          <h2 className="font-display uppercase leading-[0.82] tracking-tighter text-[15vw] lg:text-[12vw] text-white/95 select-none">
            Saver Plus
            <span className="block text-white/30">Lighting Corp.</span>
          </h2>
        </div>

        <div className="mt-6 py-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs uppercase tracking-[0.14em] text-white/40">
          <span>© {new Date().getFullYear()} Saver Plus Lighting Corp.</span>
          <span>Serving Canada since {CONTACT.since}</span>
          <span>Toronto · Ontario</span>
        </div>
      </div>
    </footer>
  );
}
